import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RootState } from 'store/reducers';
import { AmountFilter } from 'components/custom-amount-filter/CustomAmountFilter';
import { AmountFilterOperation } from 'constants/common';
import { JobStatus } from './constant';
import { TeamView } from 'containers/dispatch/dispatch.model';
import { JobsInit } from './jobs.model';

export interface JobsFilter {
  customerIds: string[];
  assignees: string[];
  createdBy: string[];
  status: JobStatus[];
  leadSource: any;
  scheduledFrom: string | null;
  scheduledTo: string | null;
  amount: AmountFilter;
  isToday?: boolean;
  schedulePreset?: string;
  externalCompany: string[];
  tags: string[];
  isUnscheduled?: boolean;
  groupByStatus?: boolean;
  serviceAreaId: string[];
}

interface JobsState {
  jobsCurrentActiveView: TeamView | null;
  jobsCurrentViewFilters: Record<keyof JobsFilter, string[] | string | number | AmountFilter | boolean | null>;
  jobsInit: JobsInit | null;
  viewFiltersMap: Record<string, Record<keyof JobsFilter, string[] | string | number | AmountFilter | boolean | null>>;
}

export const jobsCurrentViewFiltersInitialState: Record<
  keyof JobsFilter,
  string[] | string | number | AmountFilter | boolean | null
> = {
  customerIds: [],
  assignees: [],
  createdBy: [],
  status: [],
  leadSource: [],
  scheduledFrom: null,
  scheduledTo: null,
  amount: {
    filterOperation: AmountFilterOperation.ALL,
    minAmount: null,
    maxAmount: null
  },
  isToday: false,
  schedulePreset: null,
  externalCompany: [],
  tags: [],
  isUnscheduled: false,
  groupByStatus: false,
  serviceAreaId: []
};

const initialState: JobsState = {
  jobsCurrentActiveView: null,
  jobsCurrentViewFilters: jobsCurrentViewFiltersInitialState,
  jobsInit: null,
  viewFiltersMap: {}
};

const jobsSlice = createSlice({
  name: 'jobs',
  initialState,
  reducers: {
    setJobsCurrentActiveView: (state, action: PayloadAction<TeamView | null>) => {
      state.jobsCurrentActiveView = action.payload;
    },
    setJobsCurrentViewFilters: (
      state,
      action: PayloadAction<Record<keyof JobsFilter, string[] | string | number | AmountFilter | boolean | null>>
    ) => {
      state.jobsCurrentViewFilters = action.payload;
      if (state.jobsCurrentActiveView) {
        state.viewFiltersMap[state.jobsCurrentActiveView.id.toString()] = action.payload;
      }
    },
    setViewSpecificFilters: (
      state,
      action: PayloadAction<{
        viewId: string;
        filters: Record<keyof JobsFilter, string[] | string | number | AmountFilter | boolean | null>;
      }>
    ) => {
      const { viewId, filters } = action.payload;
      state.viewFiltersMap[viewId] = filters;
      if (state.jobsCurrentActiveView && state.jobsCurrentActiveView.id.toString() === viewId) {
        state.jobsCurrentViewFilters = filters;
      }
    },
    loadViewFilters: (state, action: PayloadAction<string>) => {
      const viewId = action.payload;
      const viewFilters = state.viewFiltersMap[viewId];
      if (viewFilters) {
        state.jobsCurrentViewFilters = viewFilters;
      } else {
        state.jobsCurrentViewFilters = jobsCurrentViewFiltersInitialState;
      }
    },
    resetJobsCurrentViewFilters: state => {
      state.jobsCurrentViewFilters = jobsCurrentViewFiltersInitialState;
      if (state.jobsCurrentActiveView) {
        delete state.viewFiltersMap[state.jobsCurrentActiveView.id.toString()];
      }
    },
    resetJobsActiveView: state => {
      state.jobsCurrentActiveView = null;
      state.jobsCurrentViewFilters = jobsCurrentViewFiltersInitialState;
    },
    clearAllViewFilters: state => {
      state.viewFiltersMap = {};
      state.jobsCurrentViewFilters = jobsCurrentViewFiltersInitialState;
    },
    setJobsInit: (state, action: PayloadAction<JobsInit>) => {
      state.jobsInit = action.payload;
    }
  }
});

export const {
  setJobsCurrentActiveView,
  setJobsCurrentViewFilters,
  setViewSpecificFilters,
  loadViewFilters,
  resetJobsCurrentViewFilters,
  resetJobsActiveView,
  clearAllViewFilters,
  setJobsInit
} = jobsSlice.actions;

export const selectJobsCurrentActiveView = (state: RootState) => state.rootReducer.jobs.jobsCurrentActiveView;
export const selectJobsCurrentViewFilters = (state: RootState) => state.rootReducer.jobs.jobsCurrentViewFilters;
export const selectJobsInit = (state: RootState) => state.rootReducer.jobs.jobsInit;
export const selectViewFiltersMap = (state: RootState) => state.rootReducer.jobs.viewFiltersMap;
export const selectViewSpecificFilters = (viewId: string) => (state: RootState) =>
  state.rootReducer.jobs.viewFiltersMap[viewId] || null;

export default jobsSlice.reducer;
